/**
 * Visibility memory: only the user's own changes, each stored with the config default it was made against.
 * A change is re-applied only while that default is unchanged; once the config moves, the config wins and the
 * entry is dropped. Shape in storage (key `mm_layer_vis`): { [themeId]: { [nodeKey]: { v, d } } }.
 */

export const STORAGE_KEY = 'mm_layer_vis';

export interface VisChange {
  /** the user's value */
  v: boolean;
  /** the default it was taken against */
  d: boolean;
}

export type VisMemory = Readonly<Record<string, Readonly<Record<string, VisChange>>>>;

export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

const isChange = (x: unknown): x is VisChange =>
  typeof x === 'object' &&
  x !== null &&
  typeof (x as Record<string, unknown>)['v'] === 'boolean' &&
  typeof (x as Record<string, unknown>)['d'] === 'boolean';

/** Parse defensively: anything malformed is dropped, never thrown. */
export function parseMemory(raw: string | null): VisMemory {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
  const out: Record<string, Record<string, VisChange>> = {};
  for (const [theme, entries] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof entries !== 'object' || entries === null) continue;
    const kept: Record<string, VisChange> = {};
    for (const [key, change] of Object.entries(entries as Record<string, unknown>)) {
      if (isChange(change)) kept[key] = { v: change.v, d: change.d };
    }
    if (Object.keys(kept).length > 0) out[theme] = kept;
  }
  return out;
}

/** Record a user change. Setting a node back to its default removes the entry. */
export function recordChange(
  memory: VisMemory,
  themeId: string,
  key: string,
  value: boolean,
  defaultValue: boolean,
): VisMemory {
  const theme = { ...(memory[themeId] ?? {}) };
  if (value === defaultValue) delete theme[key];
  else theme[key] = { v: value, d: defaultValue };
  const next = { ...memory };
  if (Object.keys(theme).length === 0) delete next[themeId];
  else next[themeId] = theme;
  return next;
}

/**
 * Apply remembered changes for a theme to its config defaults.
 * Returns the effective visibility and the memory with stale entries (default moved, or node gone) dropped.
 */
export function applyMemory(
  memory: VisMemory,
  themeId: string,
  defaults: Readonly<Record<string, boolean>>,
): { visibility: Record<string, boolean>; memory: VisMemory } {
  const visibility = { ...defaults };
  const entries = memory[themeId];
  if (!entries) return { visibility, memory };
  const kept: Record<string, VisChange> = {};
  for (const [key, change] of Object.entries(entries)) {
    const def = defaults[key];
    if (def === undefined || def !== change.d) continue;
    visibility[key] = change.v;
    kept[key] = change;
  }
  const next = { ...memory };
  if (Object.keys(kept).length === 0) delete next[themeId];
  else next[themeId] = kept;
  return { visibility, memory: next };
}

export function loadMemory(storage: StorageLike | null): VisMemory {
  try {
    return parseMemory(storage?.getItem(STORAGE_KEY) ?? null);
  } catch {
    return {};
  }
}

export function saveMemory(storage: StorageLike | null, memory: VisMemory): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    // Private mode, quota or blocked storage: memory is a convenience, so carry on without it.
  }
}

/** `window.localStorage` can itself throw on access when site data is blocked. */
export function browserStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
